/**
 * IndexedDB storage for offline-first inspection system
 *
 * Database: InspectionStorage
 * Version: 1
 *
 * Object Stores:
 * - inspections: Stores inspection form data and metadata
 * - photos: Stores compressed photo blobs
 * - signatures: Stores signature image blobs
 */

const DB_NAME = 'InspectionStorage';
const DB_VERSION = 1;

class InspectionStorage {
  constructor() {
    this.db = null;
  }

  /**
   * Initialize IndexedDB connection
   * @returns {Promise<IDBDatabase>}
   */
  async init() {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Inspections store
        if (!db.objectStoreNames.contains('inspections')) {
          const inspectionStore = db.createObjectStore('inspections', { keyPath: 'uuid' });
          inspectionStore.createIndex('status', 'status', { unique: false });
          inspectionStore.createIndex('created_at', 'created_at', { unique: false });
        }

        // Photos store
        if (!db.objectStoreNames.contains('photos')) {
          const photoStore = db.createObjectStore('photos', { keyPath: 'id', autoIncrement: true });
          photoStore.createIndex('inspection_uuid', 'inspection_uuid', { unique: false });
          photoStore.createIndex('question_key', 'question_key', { unique: false });
          photoStore.createIndex('uploaded', 'uploaded', { unique: false });
        }

        // Signatures store
        if (!db.objectStoreNames.contains('signatures')) {
          const signatureStore = db.createObjectStore('signatures', { keyPath: 'id', autoIncrement: true });
          signatureStore.createIndex('inspection_uuid', 'inspection_uuid', { unique: false });
          signatureStore.createIndex('question_key', 'question_key', { unique: false });
          signatureStore.createIndex('uploaded', 'uploaded', { unique: false });
        }
      };
    });
  }

  /**
   * Save inspection data to IndexedDB
   * @param {string} uuid - Inspection UUID
   * @param {Object} data - Inspection data (responses, status, etc.)
   * @returns {Promise<void>}
   */
  async saveInspection(uuid, data) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['inspections'], 'readwrite');
      const store = transaction.objectStore('inspections');

      const inspectionData = {
        uuid,
        ...data,
        updated_at: new Date().toISOString()
      };

      // If it's a new inspection, add created_at
      if (!data.created_at) {
        inspectionData.created_at = new Date().toISOString();
      }

      const request = store.put(inspectionData);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get inspection data by UUID
   * @param {string} uuid - Inspection UUID
   * @returns {Promise<Object|null>}
   */
  async getInspection(uuid) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['inspections'], 'readonly');
      const store = transaction.objectStore('inspections');
      const request = store.get(uuid);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all inspections
   * @returns {Promise<Array>}
   */
  async getAllInspections() {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['inspections'], 'readonly');
      const store = transaction.objectStore('inspections');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all inspections with a specific status
   * @param {string} status - Status to filter by (e.g., 'submitted', 'processing')
   * @returns {Promise<Array>}
   */
  async getInspectionsByStatus(status) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['inspections'], 'readonly');
      const store = transaction.objectStore('inspections');
      const index = store.index('status');
      const request = index.getAll(status);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete inspection and all associated photos/signatures
   * @param {string} uuid - Inspection UUID
   * @returns {Promise<void>}
   */
  async deleteInspection(uuid) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['inspections', 'photos', 'signatures'], 'readwrite');

      // Delete inspection
      transaction.objectStore('inspections').delete(uuid);

      // Delete associated photos
      const photoIndex = transaction.objectStore('photos').index('inspection_uuid');
      const photoRequest = photoIndex.openCursor(IDBKeyRange.only(uuid));
      photoRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      // Delete associated signatures
      const signatureIndex = transaction.objectStore('signatures').index('inspection_uuid');
      const signatureRequest = signatureIndex.openCursor(IDBKeyRange.only(uuid));
      signatureRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Save photo to IndexedDB
   * @param {string} inspectionUuid - Inspection UUID
   * @param {string} questionKey - Question key for this photo
   * @param {string} base64Data - Compressed photo as base64 data URL
   * @param {number} orderPosition - Display order
   * @returns {Promise<number>} - Photo ID
   */
  async savePhoto(inspectionUuid, questionKey, base64Data, orderPosition) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['photos'], 'readwrite');
      const store = transaction.objectStore('photos');

      const photoData = {
        inspection_uuid: inspectionUuid,
        question_key: questionKey,
        base64: base64Data, // Store as base64 string for mobile compatibility
        order_position: orderPosition,
        compressed: true,
        uploaded: false,
        created_at: new Date().toISOString()
      };

      const request = store.add(photoData);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all photos for an inspection
   * @param {string} inspectionUuid - Inspection UUID
   * @returns {Promise<Array>}
   */
  async getPhotos(inspectionUuid) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['photos'], 'readonly');
      const store = transaction.objectStore('photos');
      const index = store.index('inspection_uuid');
      const request = index.getAll(inspectionUuid);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get photos by question key
   * @param {string} inspectionUuid - Inspection UUID
   * @param {string} questionKey - Question key
   * @returns {Promise<Array>}
   */
  async getPhotosByQuestion(inspectionUuid, questionKey) {
    const photos = await this.getPhotos(inspectionUuid);
    return photos.filter(photo => photo.question_key === questionKey);
  }

  /**
   * Mark photo as uploaded
   * @param {number} photoId - Photo ID
   * @returns {Promise<void>}
   */
  async markPhotoUploaded(photoId) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['photos'], 'readwrite');
      const store = transaction.objectStore('photos');
      const request = store.get(photoId);

      request.onsuccess = () => {
        const photo = request.result;
        if (photo) {
          photo.uploaded = true;
          photo.uploaded_at = new Date().toISOString();
          store.put(photo);
        }
      };

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Delete photo from IndexedDB
   * @param {number} photoId - Photo ID
   * @returns {Promise<void>}
   */
  async deletePhoto(photoId) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['photos'], 'readwrite');
      const store = transaction.objectStore('photos');
      const request = store.delete(photoId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save signature to IndexedDB
   * @param {string} inspectionUuid - Inspection UUID
   * @param {string} questionKey - Question key for this signature
   * @param {string} base64Data - Signature image as base64 data URL
   * @returns {Promise<number>} - Signature ID
   */
  async saveSignature(inspectionUuid, questionKey, base64Data) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['signatures'], 'readwrite');
      const store = transaction.objectStore('signatures');

      const signatureData = {
        inspection_uuid: inspectionUuid,
        question_key: questionKey,
        base64: base64Data, // Store as base64 string instead of blob for mobile compatibility
        uploaded: false,
        created_at: new Date().toISOString()
      };

      const request = store.add(signatureData);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all signatures for an inspection
   * @param {string} inspectionUuid - Inspection UUID
   * @returns {Promise<Array>}
   */
  async getSignatures(inspectionUuid) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['signatures'], 'readonly');
      const store = transaction.objectStore('signatures');
      const index = store.index('inspection_uuid');
      const request = index.getAll(inspectionUuid);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Mark signature as uploaded
   * @param {number} signatureId - Signature ID
   * @returns {Promise<void>}
   */
  async markSignatureUploaded(signatureId) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['signatures'], 'readwrite');
      const store = transaction.objectStore('signatures');
      const request = store.get(signatureId);

      request.onsuccess = () => {
        const signature = request.result;
        if (signature) {
          signature.uploaded = true;
          signature.uploaded_at = new Date().toISOString();
          store.put(signature);
        }
      };

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Delete signature from IndexedDB
   * @param {number} signatureId - Signature ID
   * @returns {Promise<void>}
   */
  async deleteSignature(signatureId) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['signatures'], 'readwrite');
      const store = transaction.objectStore('signatures');
      const request = store.delete(signatureId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get count of pending uploads (photos and signatures not yet uploaded)
   * @param {string} inspectionUuid - Inspection UUID
   * @returns {Promise<{photos: number, signatures: number}>}
   */
  async getPendingUploadCounts(inspectionUuid) {
    const photos = await this.getPhotos(inspectionUuid);
    const signatures = await this.getSignatures(inspectionUuid);

    return {
      photos: photos.filter(p => !p.uploaded).length,
      signatures: signatures.filter(s => !s.uploaded).length
    };
  }

  /**
   * Clear all data from IndexedDB (useful for testing)
   * @returns {Promise<void>}
   */
  async clearAll() {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['inspections', 'photos', 'signatures'], 'readwrite');

      transaction.objectStore('inspections').clear();
      transaction.objectStore('photos').clear();
      transaction.objectStore('signatures').clear();

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }
}

// Export singleton instance
const inspectionStorageInstance = new InspectionStorage();

// Add global cleanup function for debugging orphaned inspections
if (typeof window !== 'undefined') {
  window.clearAllInspections = async function() {
    console.log('🗑️ Clearing all inspections from IndexedDB...');
    try {
      const allInspections = await inspectionStorageInstance.getAllInspections();
      console.log(`Found ${allInspections.length} inspections to delete`);

      for (const inspection of allInspections) {
        await inspectionStorageInstance.deleteInspection(inspection.uuid);
        console.log(`✅ Deleted inspection: ${inspection.uuid}`);
      }

      console.log('✅ All inspections cleared!');
      console.log('Reload the page to see changes.');
      return `Cleared ${allInspections.length} inspections`;
    } catch (error) {
      console.error('❌ Error clearing inspections:', error);
      return 'Error: ' + error.message;
    }
  };
}

export default inspectionStorageInstance;
